<script>
/* eslint-disable @intlify/vue-i18n/no-raw-text, vue/no-bare-strings-in-template */
import KefubaoAPI from 'dashboard/api/kefubao';
import KefubaoPhrasesAPI from 'dashboard/api/kefubaoPhrases';
import { useAlert } from 'dashboard/composables';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const STOP_WORDS = new Set(
  'a an and are as at be been but by can could dear do does did for from has have hello hi i if in into is it its me my not of on or our please player players provide send so that the their them this to us was we will with you your add get got'.split(
    ' '
  )
);

const MATCH_GROUPS = [
  {
    words:
      'deposit recharge payment paid credited credit topup bank receipt transaction utr upi 充值 存款 入账 到账 未到账 扣款 付款 支付 回执 凭证 交易 जमा भुगतान रिचार्ज बैंक रसीद'.split(
        ' '
      ),
    weight: 28,
  },
  {
    words:
      'withdraw withdrawal cashout refund reject rejected pending 提现 出款 取款 退款 驳回 拒绝 待处理 निकासी रिफंड अस्वीकार लंबित'.split(
        ' '
      ),
    weight: 24,
  },
  {
    words:
      'network internet connection connect lag slow loading 网络 联网 连接 卡顿 加载 नेट नेटवर्क इंटरनेट कनेक्शन'.split(
        ' '
      ),
    weight: 22,
  },
  {
    words:
      'login password account ban blocked freeze frozen otp verify verification kyc 登录 密码 账号 账户 封禁 冻结 otp 验证 खाता पासवर्ड लॉगिन प्रतिबंध सत्यापन'.split(
        ' '
      ),
    weight: 22,
  },
  {
    words:
      'reward rebate bonus loss activity promotion vip 返利 亏损 奖励 活动 优惠 档位 门槛 बोनस इनाम नुकसान'.split(
        ' '
      ),
    weight: 20,
  },
  {
    words:
      'appeal email mail template unban complaint 邮件 申诉 模板 解封 投诉 ईमेल अपील शिकायत'.split(
        ' '
      ),
    weight: 20,
  },
];

const normalize = text =>
  String(text || '')
    .toLowerCase()
    .replace(/[\u201c\u201d\u2018\u2019"'`]/g, '')
    .replace(/[，。！？；：、,.!?;:()[\]{}<>|/@#$%^&*_+=~-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const expandQuery = text => {
  const q = normalize(text);
  if (!q) return '';
  const extra = [];
  MATCH_GROUPS.forEach(group => {
    if (group.words.some(word => q.includes(word))) {
      extra.push(...group.words);
    }
  });
  return [q, ...extra].join(' ');
};

const tokens = text => {
  const raw = normalize(text);
  const words = raw.match(/[a-z0-9]+|[\u0900-\u097f]+|[\u4e00-\u9fff]/g) || [];
  const output = [];
  const seen = new Set();
  words.forEach(word => {
    if (word.length > 1 && !STOP_WORDS.has(word) && !seen.has(word)) {
      seen.add(word);
      output.push(word);
    }
  });
  const chinese = (raw.match(/[\u4e00-\u9fff]+/g) || []).join('');
  for (let i = 0; i < chinese.length - 1; i += 1) {
    const token = chinese.slice(i, i + 2);
    if (!seen.has(token)) {
      seen.add(token);
      output.push(token);
    }
  }
  return output;
};

const detectLanguage = text => {
  if (/[\u0900-\u097f]/.test(text)) return 'hi';
  if (/[A-Za-z]/.test(text)) return 'en';
  return 'zh';
};

const languageLabel = lang =>
  ({ zh: '中文', en: '英语', hi: '印地语' })[lang] || '完整话术';

const searchableContent = item => {
  const reply = item.reply || {};
  return [
    item.title || '',
    item.category1 || '',
    item.category2 || '',
    item.keywords || '',
    reply.zh || '',
    reply.en || '',
    reply.hi || '',
    item.source_content || '',
  ].join(' ');
};

const replyForLanguage = (item, lang) => {
  const reply = item.reply || {};
  return reply[lang] || reply.en || reply.hi || reply.zh || item.content || '';
};

const hasAnyWord = (text, words) => words.some(word => text.includes(word));

const scoreSingleQuery = (query, item, weight = 1) => {
  const q = normalize(query);
  if (!q) return 0;
  const expandedQ = expandQuery(q);
  const title = normalize(
    [item.title, item.category1, item.category2].filter(Boolean).join(' ')
  );
  const content = normalize(searchableContent(item));
  let score = 0;
  if (title && (title.includes(q) || q.includes(title))) score += 80;
  const titleSet = new Set(tokens(title));
  const contentSet = new Set(tokens(content));
  const depositQuery = hasAnyWord(q, MATCH_GROUPS[0].words);
  const withdrawQuery = hasAnyWord(q, MATCH_GROUPS[1].words);
  const depositTitle = hasAnyWord(title, MATCH_GROUPS[0].words);
  const withdrawTitle = hasAnyWord(title, MATCH_GROUPS[1].words);
  if (depositQuery && depositTitle) score += 36;
  if (withdrawQuery && withdrawTitle) score += 36;
  if (depositQuery && withdrawTitle) score -= 48;
  if (withdrawQuery && depositTitle) score -= 48;
  tokens(expandedQ).forEach(token => {
    if (titleSet.has(token)) score += token.length >= 2 ? 12 : 3;
    if (contentSet.has(token)) score += token.length >= 2 ? 4 : 1;
  });
  MATCH_GROUPS.forEach(group => {
    const queryHit = group.words.some(word => expandedQ.includes(word));
    if (!queryHit) return;
    const contentHit = group.words.some(word => content.includes(word));
    const titleHit = group.words.some(word => title.includes(word));
    if (titleHit) score += group.weight;
    else if (contentHit) score += Math.round(group.weight * 0.65);
  });
  return score * weight;
};

const scoreItem = (queries, item) => {
  const list = Array.isArray(queries)
    ? queries
    : [{ text: queries, weight: 1 }];
  return list.reduce((total, query) => {
    if (!query) return total;
    if (typeof query === 'string') return total + scoreSingleQuery(query, item);
    return total + scoreSingleQuery(query.text, item, query.weight);
  }, 0);
};

const matchQueries = (message, translation) =>
  [
    { text: message, weight: 1 },
    translation ? { text: translation, weight: 1.15 } : null,
  ].filter(Boolean);

const isIncomingMessage = message =>
  message?.message_type === 0 || message?.message_type === 'incoming';

export default {
  name: 'KefubaoAssistant',
  components: {
    Icon,
  },
  props: {
    currentChat: { type: Object, default: () => ({}) },
    disabled: { type: Boolean, default: false },
  },
  emits: ['insert', 'toggleEditorSize'],
  data() {
    return {
      isOpen: false,
      translation: '',
      isTranslating: false,
      isGenerating: false,
      isLoadingPhrases: false,
      phrasebook: [],
      aiReplies: [],
      aiCacheKey: '',
      useFullIncomingContext: false,
    };
  },
  computed: {
    incomingTextMessages() {
      const messages = this.currentChat?.messages || [];
      return messages
        .filter(isIncomingMessage)
        .map(message => String(message.content || '').trim())
        .filter(Boolean);
    },
    latestIncomingMessage() {
      const messages = this.currentChat?.messages || [];
      const incoming = [...messages]
        .reverse()
        .find(message => isIncomingMessage(message) && message.content);
      return String(incoming?.content || '').trim();
    },
    fullIncomingContext() {
      return this.incomingTextMessages
        .map((message, index) => `${index + 1}. ${message}`)
        .join('\n');
    },
    activeIncomingMessage() {
      return this.useFullIncomingContext
        ? this.fullIncomingContext
        : this.latestIncomingMessage;
    },
    activeIncomingPreview() {
      return this.activeIncomingMessage || '暂无玩家消息';
    },
    detectedLanguage() {
      return detectLanguage(this.activeIncomingMessage);
    },
    activeMatchQueries() {
      if (!this.activeIncomingMessage) return [];
      if (!this.useFullIncomingContext) {
        return matchQueries(this.activeIncomingMessage, this.translation);
      }

      const messageQueries = this.incomingTextMessages.map(
        (message, index) => ({
          text: message,
          weight: 0.8 + index * 0.08,
        })
      );
      const latestMessage = this.latestIncomingMessage
        ? { text: this.latestIncomingMessage, weight: 1.35 }
        : null;

      return [
        { text: this.fullIncomingContext, weight: 0.9 },
        ...messageQueries,
        latestMessage,
        this.translation ? { text: this.translation, weight: 1.2 } : null,
      ].filter(Boolean);
    },
    matches() {
      if (!this.activeIncomingMessage) return [];
      return this.phrasebook
        .map(item => ({
          item,
          score: scoreItem(this.activeMatchQueries, item),
        }))
        .filter(result => result.score > 0)
        .sort((a, b) => b.score - a.score)
        .slice(0, 6)
        .map(result => {
          const reply = replyForLanguage(result.item, this.detectedLanguage);
          return {
            id: `match-${result.item.id || result.item.row}`,
            title:
              result.item.title ||
              result.item.category2 ||
              result.item.category1 ||
              '话术',
            score: Math.round(result.score),
            language: this.detectedLanguage,
            text: reply,
            zh:
              this.detectedLanguage === 'zh' ? '' : result.item.reply?.zh || '',
          };
        });
    },
    aiCards() {
      return this.aiReplies.map((reply, index) => ({
        id: `ai-${index}`,
        title: `AI 快捷回复 ${index + 1}`,
        text: reply.text,
        zh: reply.zh,
      }));
    },
  },
  watch: {
    activeIncomingMessage() {
      this.translation = '';
      this.aiReplies = [];
      this.aiCacheKey = '';
      if (this.isOpen) this.translateCurrentMessage();
    },
    isOpen(value) {
      if (value) this.loadPhrases();
      if (value && this.activeIncomingMessage && !this.translation) {
        this.translateCurrentMessage();
      }
    },
  },
  mounted() {
    this.loadPhrases();
  },
  methods: {
    languageLabel,
    insert(text) {
      this.$emit('insert', text);
    },
    async loadPhrases() {
      this.isLoadingPhrases = true;
      try {
        const { data } = await KefubaoPhrasesAPI.get();
        this.phrasebook = data || [];
      } catch (error) {
        useAlert(error?.response?.data?.error || '客服话术加载失败');
      } finally {
        this.isLoadingPhrases = false;
      }
    },
    async copy(text) {
      await navigator.clipboard.writeText(text);
      useAlert('已复制');
    },
    async translateCurrentMessage() {
      if (!this.activeIncomingMessage) return;
      this.isTranslating = true;
      try {
        const { data } = await KefubaoAPI.translate({
          text: this.activeIncomingMessage,
          targetLanguage: 'zh',
        });
        this.translation = data.content || '';
      } catch (error) {
        useAlert(error?.response?.data?.error || '翻译失败');
      } finally {
        this.isTranslating = false;
      }
    },
    async translateReply(text) {
      const { data } = await KefubaoAPI.translate({
        text,
        targetLanguage: 'zh',
      });
      return data.content || '';
    },
    async generateAiReplies() {
      if (!this.activeIncomingMessage) {
        useAlert('没有可识别的玩家消息');
        return;
      }
      const cacheKey = `${this.useFullIncomingContext ? 'full' : 'latest'}:${normalize(this.activeIncomingMessage)}:${this.detectedLanguage}`;
      if (this.aiCacheKey === cacheKey && this.aiReplies.length) return;
      this.isGenerating = true;
      try {
        const { data } = await KefubaoAPI.quickReplies({
          text: this.activeIncomingMessage,
          language: this.detectedLanguage === 'hi' ? 'hi' : 'en',
        });
        const replies = (data.replies || []).slice(0, 2);
        this.aiReplies = await Promise.all(
          replies.map(async reply => ({
            text: reply,
            zh: await this.translateReply(reply).catch(() => ''),
          }))
        );
        this.aiCacheKey = cacheKey;
      } catch (error) {
        useAlert(error?.response?.data?.error || 'AI 快捷回复生成失败');
      } finally {
        this.isGenerating = false;
      }
    },
  },
};
</script>

<template>
  <div class="border-b border-n-weak bg-n-solid-1">
    <div class="flex flex-wrap items-center gap-2 px-3 py-2">
      <button
        class="flex h-8 items-center gap-1.5 rounded-lg bg-n-brand px-3 text-sm font-semibold text-white shadow-sm transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-60"
        type="button"
        :disabled="disabled"
        @click="isOpen = !isOpen"
      >
        <Icon icon="i-lucide-message-square-text" class="size-4" />
        客服宝助手
        <Icon
          :icon="isOpen ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
          class="size-4"
        />
      </button>
      <span class="min-w-0 flex-1 truncate text-xs text-n-slate-11">
        {{ useFullIncomingContext ? '全部客户消息：' : '当前用户消息：' }}
        {{ isLoadingPhrases ? '正在加载客服话术...' : activeIncomingPreview }}
      </span>
      <div
        class="flex h-8 items-center rounded-lg border border-n-weak bg-n-solid-2 p-0.5"
      >
        <button
          class="inline-flex h-7 items-center gap-1.5 rounded-md px-2.5 text-xs font-medium transition disabled:cursor-not-allowed disabled:opacity-60"
          :class="
            useFullIncomingContext
              ? 'bg-n-brand text-white shadow-sm'
              : 'text-n-slate-11 hover:bg-n-alpha-black2 hover:text-n-slate-12'
          "
          type="button"
          :disabled="!incomingTextMessages.length"
          @click="useFullIncomingContext = true"
        >
          <Icon icon="i-lucide-list-tree" class="size-3.5" />
          分析全部消息
        </button>
        <button
          class="inline-flex h-7 items-center gap-1.5 rounded-md px-2.5 text-xs font-medium transition disabled:cursor-not-allowed disabled:opacity-60"
          :class="
            !useFullIncomingContext
              ? 'bg-n-brand text-white shadow-sm'
              : 'text-n-slate-11 hover:bg-n-alpha-black2 hover:text-n-slate-12'
          "
          type="button"
          :disabled="!incomingTextMessages.length"
          @click="useFullIncomingContext = false"
        >
          <Icon icon="i-lucide-message-square" class="size-3.5" />
          只看最后一条
        </button>
      </div>
      <span
        class="inline-flex h-8 items-center gap-1.5 rounded-lg border border-n-teal-6 bg-n-teal-2 px-3 text-xs font-semibold text-n-teal-11"
      >
        <Icon icon="i-lucide-bot" class="size-3.5" />
        OpenRouter AI
      </span>
      <button
        class="inline-flex size-8 items-center justify-center rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-11 transition hover:border-n-brand hover:text-n-brand"
        type="button"
        title="展开/收起回复框"
        @click="$emit('toggleEditorSize')"
      >
        <Icon icon="i-lucide-maximize-2" class="size-4" />
      </button>
    </div>

    <div v-if="isOpen" class="px-3 pb-3 space-y-3">
      <div
        class="rounded-md border border-n-weak bg-n-alpha-black2 p-2 text-xs text-n-slate-11"
      >
        <div class="font-medium text-n-slate-12">玩家消息中文参考</div>
        <div class="mt-1 whitespace-pre-wrap">
          <span v-if="isTranslating">正在翻译...</span>
          <span v-else-if="translation">{{ translation }}</span>
          <span v-else>{{
            activeIncomingMessage
              ? 'OpenRouter AI 将自动翻译玩家消息'
              : '暂无玩家消息'
          }}</span>
        </div>
      </div>

      <div class="flex items-center justify-between">
        <div class="text-sm font-semibold text-n-slate-12">AI 快捷回复</div>
        <button
          class="inline-flex h-8 items-center gap-1.5 rounded-lg border border-n-blue-6 bg-n-blue-2 px-3 text-xs font-semibold text-n-blue-11 transition hover:border-n-blue-8 hover:bg-n-blue-3 disabled:cursor-not-allowed disabled:opacity-60"
          type="button"
          :disabled="isGenerating"
          @click="generateAiReplies"
        >
          <Icon
            :icon="
              isGenerating ? 'i-lucide-loader-circle' : 'i-lucide-sparkles'
            "
            class="size-3.5"
            :class="{ 'animate-spin': isGenerating }"
          />
          {{
            isGenerating
              ? '生成中...'
              : aiCards.length
                ? '重新生成'
                : '生成两条'
          }}
        </button>
      </div>
      <div v-if="aiCards.length" class="grid gap-2">
        <div
          v-for="card in aiCards"
          :key="card.id"
          class="rounded-md border border-n-teal-5 bg-n-teal-2 p-2"
        >
          <div class="text-sm font-semibold text-n-slate-12">
            {{ card.title }}
          </div>
          <div class="mt-1 whitespace-pre-wrap text-sm text-n-slate-12">
            {{ card.text }}
          </div>
          <div
            v-if="card.zh"
            class="mt-2 border-t border-n-weak pt-2 text-xs text-n-slate-11"
          >
            中文翻译：{{ card.zh }}
          </div>
          <div class="mt-2 flex gap-2">
            <button
              class="h-7 px-3 rounded-md bg-n-brand text-white text-xs"
              type="button"
              @click="insert(card.text)"
            >
              填入回复
            </button>
            <button
              class="h-7 px-3 rounded-md border border-n-weak text-xs"
              type="button"
              @click="copy(card.text)"
            >
              复制
            </button>
          </div>
        </div>
      </div>

      <div class="text-sm font-semibold text-n-slate-12">
        匹配话术
        <span class="ml-1 text-xs font-normal text-n-slate-11">
          找到 {{ matches.length }} 条，将使用：{{
            languageLabel(detectedLanguage)
          }}
        </span>
      </div>
      <div class="grid max-h-80 gap-2 overflow-y-auto pr-1">
        <div
          v-for="match in matches"
          :key="match.id"
          class="rounded-md border border-n-weak bg-n-solid-2 p-2"
        >
          <div class="text-sm font-semibold text-n-slate-12">
            {{ match.title }} · {{ match.score }}分 ·
            {{ languageLabel(match.language) }}
          </div>
          <div class="mt-1 whitespace-pre-wrap text-sm text-n-slate-12">
            {{ match.text }}
          </div>
          <div
            v-if="match.zh"
            class="mt-2 border-t border-n-weak pt-2 text-xs text-n-slate-11"
          >
            中文翻译：{{ match.zh }}
          </div>
          <div class="mt-2 flex gap-2">
            <button
              class="h-7 px-3 rounded-md bg-n-brand text-white text-xs"
              type="button"
              @click="insert(match.text)"
            >
              填入回复
            </button>
            <button
              class="h-7 px-3 rounded-md border border-n-weak text-xs"
              type="button"
              @click="copy(match.text)"
            >
              复制
            </button>
          </div>
        </div>
        <div
          v-if="!matches.length"
          class="rounded-md border border-n-weak p-3 text-sm text-n-slate-11"
        >
          暂无匹配话术。
        </div>
      </div>
    </div>
  </div>
</template>
